import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import QuestionShort from "./QuestionShort";
import QuestionMultiple from "./QuestionMultiple";
import QuestionScale from "./QuestionScale";
import { ProgressPictureAnswer } from "./QuestionProgressPictures";
import convert from "convert-units";
import { round } from "lodash";
import SummaryDailyStats from "../../metrics/check-in-summary/components/SummaryDailyStats";

const OnboardingSummary = ({ onboardingInfo, setOnboardingInfo, locals }) => {
  const { t } = useTranslation("athlete-portal");
  const { onboarding } = onboardingInfo;
  const { activeUser, query } = locals;
  const units = activeUser.units || "metric";
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";

  const reversedAnswers = onboarding.answers.slice().reverse();

  const stepsArray = onboarding.answers.find(
    (answer) => answer.type === "steps"
  );
  const sleepArray = onboarding.answers.find(
    (answer) => answer.type === "sleep"
  );
  const hrvArray = onboarding.answers.find((answer) => answer.type === "hrv");

  const dailyStats = mergeHealthData(
    stepsArray?.answer?.value,
    sleepArray?.answer?.value,
    hrvArray?.answer?.value
  );

  return (
    <div className="onboarding-questionnaire">
      <div className="flex gap-4 mb-8">
        <button
          className="rd-back"
          onClick={() => {
            setOnboardingInfo(null);
          }}
        ></button>
        <div>
          <h4>{t("onboardingSummary.title")}</h4>
          <p className="p5">
            {t("onboardingSummary.completedOn")}{" "}
            {moment(onboardingInfo.utcDate, "YYYYMMDD").format("MMM D, YYYY")}
          </p>
        </div>
      </div>

      {reversedAnswers.map((answer) => (
        <DisplaySingleQuestion
          singleAnswer={answer}
          onboardingInfo={onboardingInfo}
          units={units}
          t={t}
          key={answer._id}
        />
      ))}

      <div id="check-in-summary">
        <SummaryDailyStats
          athlete={locals.athlete}
          date={moment(onboardingInfo.utcDate, "YYYYMMDD").format("YYYY-MM-DD")}
          organizationCoachParam={organizationCoachParam}
          dailyStats={dailyStats}
        />
      </div>
    </div>
  );
};

const DisplaySingleQuestion = ({ singleAnswer, onboardingInfo, units, t }) => {
  const { type, answer, questionBody, questionTitle } = singleAnswer;

  // Cuz we are already showing these without the need for a question
  if (type === "sleep" || type === "steps" || type === "hrv") return null;

  if (type === "measurement") {
    const atLeastOneMeasurement = Object.values(answer.value).some(
      (value) => Number(value) !== 0
    );

    if (!atLeastOneMeasurement) return null;
  }

  const questionDisplayTitle = getQuestionTitle(type, questionTitle, t);

  const questionDisplayBody = getQuestionBody(
    type,
    questionBody,
    answer,
    onboardingInfo,
    units,
    t
  );

  return (
    <div className={"questionnaire-question"}>
      <div className="question-header">
        <h4>{questionDisplayTitle}</h4>
      </div>
      <div className="question-body">{questionDisplayBody}</div>
    </div>
  );
};

const getQuestionTitle = (type, questionTile, t) => {
  switch (type) {
    case "short":
    case "multiple":
    case "checkbox":
    case "scale":
    case "measurement":
      return questionTile;
    case "bodyweight":
      return t("metrics.bodyweight");
    case "nutrition":
      return t("onboardingSummary.nutritionSummary");
    case "progress":
      return t("progressPhotos.title");
  }
};

const getQuestionBody = (type, body, answer, onboardingInfo, units, t) => {
  if (!answer && type !== "progress") {
    return <div>{t("onboardingSummary.errorDisplaying")}</div>;
  }

  switch (type) {
    case "short":
      return <QuestionShort description={answer.value} />;
    case "multiple":
    case "checkbox":
      return (
        <QuestionMultiple
          type={body.type}
          options={body.options}
          answers={answer.value}
        />
      );
    case "scale":
      return (
        <QuestionScale
          labelLeft={body.labelLeft}
          labelRight={body.labelRight}
          answer={answer.value}
        />
      );
    case "bodyweight":
      const bodyweight = answer.value.bodyweight;
      const isPounds = answer.value.isPounds;
      const label = units === "metric" ? "Kg" : "Lbs";
      const isMetric = units === "metric";

      const displayWeight = isPounds
        ? isMetric
          ? convert(bodyweight).from("lb").to("kg")
          : bodyweight
        : isMetric
        ? bodyweight
        : convert(bodyweight).from("kg").to("lb");

      return (
        <QuestionShort description={`${round(displayWeight, 2)} ${label}`} />
      );
    case "progress":
      return <ProgressPictureAnswer dailyStats={[onboardingInfo]} />;
    case "nutrition":
      const nutritionData = answer.value;

      return (
        <section
          role="table-wrapper"
          className="nutrition-table"
          data-scrollable
        >
          <table role="report-table">
            <thead>
              <tr>
                <th>{t("bodyMeasurements.date")}</th>
                <th className="protein">
                  {t("statInformation.protein")}
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th className="carbs">
                  {t("statInformation.carbs")}
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th className="fat">
                  {t("onboardingSummary.fat")}
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th>
                  {t("onboardingSummary.fiber")}
                  <br />
                  <p className="st1">(g)</p>
                </th>
                <th>
                  {t("onboardingSummary.kcal")}
                  <br />
                  <p className="st1"></p>
                </th>
              </tr>
            </thead>

            <tbody>
              {nutritionData.map((data) => (
                <tr key={`onboarding-nutrition-${data.utcDate}`}>
                  <td>
                    {moment(data.utcDate, "YYYYMMDD").format("MMM D, YYYY")}
                  </td>
                  <td>{round(data.proteins, 0) || "-"}</td>
                  <td>{round(data.carbs, 0) || "-"}</td>
                  <td>{round(data.fats, 0) || "-"}</td>
                  <td>{round(data.fibers, 0) || "-"}</td>
                  <td>
                    {round(
                      data.proteins * 4 + data.carbs * 4 + data.fats * 9,
                      0
                    ) || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );

    case "measurement":
      const { value: measurements } = answer;
      const unitLabel = units === "metric" ? "cm" : "inch";

      const tableBody = Object.entries(measurements).map(
        ([partName, value]) => {
          const convertedValue = units === "metric" ? value : value / 2.54;
          return {
            text: t(`bodyMeasurements.bodyParts.${partName}`, { defaultValue: partName }),
            value: round(convertedValue, 2),
          };
        }
      );

      return (
        <section
          role="table-wrapper"
          className="nutrition-table w-1/2"
          data-scrollable
        >
          <table role="report-table">
            <thead>
              <tr>
                <th>{t("onboardingSummary.part")}</th>
                <th>{t("onboardingSummary.measurement", { unit: unitLabel || "cm" })}</th>
              </tr>
            </thead>

            <tbody>
              {tableBody.map(({ text, value }) => (
                <tr key={`${text}-${value}-measurements`}>
                  <td>{text}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );
    default:
      return <div>{t("onboardingSummary.errorDisplaying")}</div>;
  }
};

function mergeHealthData(stepsArray, sleepArray, hrvArray) {
  const mergedData = {};

  // Merge each dataset
  addData(stepsArray, "steps", mergedData);
  addData(sleepArray, "sleep", mergedData);
  addData(hrvArray, "hrv", mergedData);

  // Convert mergedData back to an array
  return Object.values(mergedData);
}

// Helper function to add data to mergedData
function addData(array, key, mergedData) {
  if (!array) return; // Skip undefined arrays

  array.forEach((item) => {
    const { utcDate, date } = item;
    if (!mergedData[utcDate]) {
      mergedData[utcDate] = { utcDate, date };
    }
    mergedData[utcDate][key] = item[key] || 0;
  });
}

export default OnboardingSummary;
